import {
  App,
  Button,
  Table,
} from 'antd'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocalStorage } from 'react-use'

import CreatorFields from '@/components/feature/creators/components/creator-fields'
import GroupTree from '@/components/fields/group-tree'
import FunctionPermission from '@/components/permissions/function-permission'
import Page from '@/components/shared/page'
import { FunctionKey } from '@/constants/function-key'
import { CREATOR_PAGE_SIDER_COLLAPSED } from '@/constants/local-storages'
import useFormModal from '@/hooks/use-form-modal'
import { GroupType } from '@/model/group'
import {
  createCreator,
  CreateCreatorParams,
  readAllCreators,
  updateCreator,
  UpdateCreatorParams,
} from '@/redux/creator-actions'
import { selectAccessibleCreators } from '@/redux/creator-selector'
import { readAllEmployees } from '@/redux/employee-actions'
import { selectAccessibleEmployees } from '@/redux/employee-selector'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import SearchCreator, { SearchCreatorValues } from './components/search-creator'
import useCreatorColumns, { Record } from './hooks/use-creator-columns'

const selector = createAppSelector([
  selectAccessibleCreators,
  selectAccessibleEmployees,
  (state) => state.group.groups,
  (state) => state.creator.creatorsFetching,
  (state) => state.employee.employeesFetching,
], (
  creators,
  employees,
  groups,
  creatorsFetching,
  employeesFetching,
): {
  dataSource: Record[]
  fetching: boolean
} => ({
  dataSource: creators.map((creator) => {
    const group = groups.find(({ id }) => id === creator.groupId)
    return {
      ...creator,
      groupIds: group ? [...group.ancestorIds, group.id] : [],
      confirmPassword: creator.password,
      employeeNames: creator
        .employeeIds
        .map((employeeId) => employees.find(({ id }) => id === employeeId)?.name)
        .filter((item) => item !== undefined)
        .join(', '),
    }
  }),
  fetching: creatorsFetching || employeesFetching,
}),
)

export default function ManageCreators() {
  const { dataSource, fetching } = useAppSelector(selector)
  const [selectedGroupId, setSelectedGroupId] = useState<number>()
  const [searchValues, setSearchValues] = useState<SearchCreatorValues>()

  const dispatch = useAppDispatch()
  useEffect(() => {
    void dispatch(readAllCreators())
    void dispatch(readAllEmployees())
  }, [dispatch])
  const { message } = App.useApp()
  const onCreateCreator = useCallback(async (values: CreateCreatorParams) => {
    await dispatch(createCreator(values)).unwrap()
    await dispatch(readAllCreators()).unwrap()
    void message.success('Add creator success')
  }, [dispatch, message])
  const [createFormModal, openCreateFormModal] = useFormModal({
    onSubmit: onCreateCreator,
    getFields: () => <CreatorFields isCreating />,
    title: 'Add Creator',
    modalProps: { centered: true },
  })
  const onUpdateCreator = useCallback(async (values: UpdateCreatorParams) => {
    await dispatch(updateCreator(values)).unwrap()
    void message.success('Update creator success')
  }, [dispatch, message])
  const [updateFormModal, openUpdateFormModal] = useFormModal({
    onSubmit: onUpdateCreator,
    getFields: () => <CreatorFields />,
    title: 'Edit Creator',
    modalProps: { centered: true },
  })

  const columns = useCreatorColumns(openUpdateFormModal)
  const filteredDataSource = useMemo(() => {
    let result = dataSource
    if (selectedGroupId) {
      result = result.filter(({ groupIds }) => groupIds.includes(selectedGroupId))
    }
    if (searchValues) {
      const {
        name,
        employeeId,
        status,
      } = searchValues
      if (name) {
        result = result.filter((item) => item.name.toLowerCase().includes(name.toLowerCase()))
      }
      if (employeeId) {
        result = result.filter((item) => item.employeeIds.includes(employeeId))
      }
      if (status) {
        result = result.filter((item) => item.status === status)
      }
    }
    return result
  }, [dataSource, selectedGroupId, searchValues])

  const [siderCollapsed, setSiderCollapsed] = useLocalStorage(CREATOR_PAGE_SIDER_COLLAPSED, true)

  return (
    <Page
      headerRight={(
        <FunctionPermission functionKey={FunctionKey.CreatorsManageAddCreator}>
          <Button type="primary" onClick={openCreateFormModal}>+ Add Creator</Button>
        </FunctionPermission>
      )}
      siderCollapsed={siderCollapsed}
      siderCollapsible={true}
      sider={(
        <GroupTree
          editable
          type={GroupType.Creator}
          value={selectedGroupId}
          onChange={setSelectedGroupId}
          maxAncestors={1}
          ellipsis
        />
      )}
      siderWidth={260}
      onSiderCollapsedChange={setSiderCollapsed}
    >
      <SearchCreator onSearch={setSearchValues} />
      <Table
        loading={fetching}
        dataSource={filteredDataSource}
        columns={columns}
        rowKey="id"
      />
      {createFormModal}
      {updateFormModal}
    </Page>
  )
}
